import { useMemo } from 'react';
import useScheduleStore, {
  computeEffectiveDayBars,
  computeEffectiveBlockBars,
} from '../stores/useScheduleStore';
import { buildEventColorMap } from './schedule-colors';
import { ScheduleEvent, OOOEntry } from './types';

/**
 * Everything ScheduleGrid needs that is derived from store state.
 *
 * Extracted when the crew view (ff-feedback items 7+14) became the grid's second
 * consumer. Both pages need the identical six maps, and a copy in each is the
 * "two things answering one question" shape that this whole feedback arc came
 * from: they drift, and the bug reads as "the schedule disagrees with itself".
 *
 * Reads the store directly rather than taking arguments, because there is one
 * store and the two pages are never mounted at once (separate routes).
 */
export function useScheduleDerived() {
  const days = useScheduleStore(s => s.days);
  const events = useScheduleStore(s => s.events);
  const ooo = useScheduleStore(s => s.ooo);
  const assignments = useScheduleStore(s => s.assignments);
  const shifts = useScheduleStore(s => s.shifts);
  const unavailableBlocks = useScheduleStore(s => s.unavailableBlocks);
  const pendingChanges = useScheduleStore(s => s.pendingChanges);
  const pendingBlockChanges = useScheduleStore(s => s.pendingBlockChanges);

  const eventColorMap = useMemo(() => buildEventColorMap(events), [events]);

  const eventLookup = useMemo(() => {
    const map = new Map<string, ScheduleEvent>();
    events.forEach(e => map.set(e.id, e));
    return map;
  }, [events]);

  // Walks `days`, so it is correct across a month boundary. See
  // docs/features/schedule-continuous-scroll.md.
  const eventsPerDay = useMemo(() => {
    const map = new Map<string, ScheduleEvent[]>();
    for (const evt of events) {
      const end = evt.end_date || evt.event_date;
      for (const ds of days) {
        if (ds >= evt.event_date && ds <= end) {
          const arr = map.get(ds) || [];
          arr.push(evt);
          map.set(ds, arr);
        }
      }
    }
    return map;
  }, [events, days]);

  const oooByUserDay = useMemo(() => {
    const map = new Map<string, Map<string, OOOEntry[]>>();
    for (const entry of ooo) {
      let userMap = map.get(entry.user_id);
      if (!userMap) { userMap = new Map(); map.set(entry.user_id, userMap); }
      for (const ds of days) {
        if (ds >= entry.start_date && ds <= entry.end_date) {
          const arr = userMap.get(ds) || [];
          arr.push(entry);
          userMap.set(ds, arr);
        }
      }
    }
    return map;
  }, [ooo, days]);

  // Event-grain merged assignments — identity only (which events this user is on,
  // regardless of which days).
  const effectiveDayBars = useMemo(
    () => computeEffectiveDayBars(shifts, assignments, pendingChanges),
    [shifts, assignments, pendingChanges]
  );

  const effectiveBlockBars = useMemo(
    () => computeEffectiveBlockBars(unavailableBlocks, pendingBlockChanges),
    [unavailableBlocks, pendingBlockChanges]
  );

  // Over a 134-day window this is nearly always true, where over one month it
  // often was not — Collapse is now the normal way to read a long quiet stretch.
  const hasEmptyDays = useMemo(() => {
    let active = 0;
    for (const ds of days) if (eventsPerDay.has(ds)) active++;
    return active < days.length - 2;
  }, [eventsPerDay, days]);

  return {
    eventColorMap, eventLookup, eventsPerDay, oooByUserDay,
    effectiveDayBars, effectiveBlockBars, hasEmptyDays,
  };
}
